// Host side of the keep-alive postMessage contract for an embedded app
// (see docs/enquiry-keep-alive-postmessage-contract.md). Pure logic â€” the DOM
// wrapper (components/keep-alive-iframe) feeds it visibility changes and
// window messages and reacts to onRelease.

export interface KeepAliveIframeProtocol {
    // `data.source` value stamped on outgoing / expected on incoming messages
    hostSource: string;
    peerSource: string;
    visibilityType: string;
    queryDirtyType: string;
    readyType: string;
    dirtyStateType: string;
}

export interface KeepAliveIframeControllerOptions {
    // Explicit postMessage target and the only accepted event.origin.
    origin: string;
    protocol: KeepAliveIframeProtocol;
    idleReleaseMs: number;
    dirtyQueryTimeoutMs: number;
    getContentWindow: () => Window | null;
    // Called when the hidden iframe may be unmounted (not dirty).
    onRelease: () => void;
    createRequestId?: () => string;
}

type MessageLike = Pick<MessageEvent, "origin" | "source" | "data">;

export class KeepAliveIframeController {
    private hidden = false;
    private disposed = false;
    private idleTimer: ReturnType<typeof setTimeout> | null = null;
    private queryTimer: ReturnType<typeof setTimeout> | null = null;
    private pendingRequestId: string | null = null;
    private requestCounter = 0;

    constructor(private readonly options: KeepAliveIframeControllerOptions) {}

    setHidden(hidden: boolean) {
        if (this.disposed || hidden === this.hidden) return;
        this.hidden = hidden;
        this.postVisibility();
        this.cancelRelease();
        if (hidden) this.armIdleTimer();
    }

    handleMessage(event: MessageLike) {
        if (this.disposed) return;
        const { origin, protocol } = this.options;
        if (event.origin !== origin) return;
        if (!event.source || event.source !== this.options.getContentWindow()) return;
        const data = event.data;
        if (!data || typeof data !== "object" || data.source !== protocol.peerSource) return;

        switch (data.type) {
            case protocol.readyType:
                this.postVisibility();
                break;
            case protocol.dirtyStateType:
                this.onDirtyState(data.requestId, data.dirty);
                break;
            default:
                break;
        }
    }

    dispose() {
        this.disposed = true;
        this.cancelRelease();
    }

    private post(message: object) {
        const contentWindow = this.options.getContentWindow();
        if (!contentWindow) return;
        contentWindow.postMessage({ source: this.options.protocol.hostSource, ...message }, this.options.origin);
    }

    private postVisibility() {
        this.post({ type: this.options.protocol.visibilityType, hidden: this.hidden });
    }

    private armIdleTimer() {
        this.idleTimer = setTimeout(() => {
            this.idleTimer = null;
            this.onIdle();
        }, this.options.idleReleaseMs);
    }

    // Re-checks every idle period while hidden: a dirty (or silent) peer is kept.
    private onIdle() {
        this.armIdleTimer();
        this.pendingRequestId = this.options.createRequestId
            ? this.options.createRequestId()
            : `keep-alive-${Date.now()}-${++this.requestCounter}`;
        this.post({ type: this.options.protocol.queryDirtyType, requestId: this.pendingRequestId });
        this.queryTimer = setTimeout(() => {
            this.queryTimer = null;
            this.pendingRequestId = null;
        }, this.options.dirtyQueryTimeoutMs);
    }

    private onDirtyState(requestId: string, dirty: boolean) {
        if (!this.pendingRequestId || requestId !== this.pendingRequestId) return;
        if (this.queryTimer) clearTimeout(this.queryTimer);
        this.queryTimer = null;
        this.pendingRequestId = null;
        if (!dirty) {
            this.cancelRelease();
            this.options.onRelease();
        }
    }

    // Drops the idle timer and any in-flight dirty query (late answers are ignored).
    private cancelRelease() {
        if (this.idleTimer) clearTimeout(this.idleTimer);
        if (this.queryTimer) clearTimeout(this.queryTimer);
        this.idleTimer = null;
        this.queryTimer = null;
        this.pendingRequestId = null;
    }
}
